import { CompanySetUpTabHash } from 'enums/CompanySetupEnums';
import { removeFalsyKeys } from './removeFalsyKeys';
import {
  useNavigate,
  useLocation,
  createSearchParams,
  generatePath,
} from 'react-router-dom';
import { AppRoutes } from 'routes/routeConstants/appRoutes';

const useRedirect = () => {
  const navigate = useNavigate();

  const location = useLocation();

  const navigateBack = () => navigate(-1);

  const redirect = (props: {
    pathname: string;
    queryParams?: {};
    hash?: string;
    replace?: boolean;
    state?: string;
  }) => {
    const { pathname, queryParams, hash, replace, state } = props;
    navigate(
      {
        pathname,
        search:
          queryParams &&
          createSearchParams(removeFalsyKeys(queryParams)).toString(),
        hash,
      },
      { replace, state }
    );
  };

  const redirectToEditOrganization = (params?: string) =>
    redirect({
      pathname: AppRoutes.EDIT_ORGANISATION_MAP,
      queryParams: { year: params },
    });

  const redirectToActivityDataEntry = (
    id?: string,
    params?: {
      activeTab?: string;
      isEditView?: boolean;
      isFirstFlow?: boolean;
      scopeTab?: string;
    },
    replace?: boolean
  ) =>
    redirect({
      pathname: generatePath(AppRoutes.ACTIVITY_DATA_ENTRY, { id }),
      queryParams: params,
      replace,
    });

  const redirectToAddLocations = (params?: {
    isProcessingFacility?: boolean;
    belongsId?: string;
  }) => redirect({ pathname: AppRoutes.ADD_LOCATIONS, queryParams: params });

  const redirectToCompanySetUp =
    (hash: CompanySetUpTabHash, params?: { year?: string }) => () =>
      redirect({
        pathname: generatePath(AppRoutes.COMPANY_SETUP),
        queryParams: params,
        hash,
      });

  const redirectToOrganizationDetails = (
    id: string,
    params?: { year?: number }
  ) =>
    redirect({
      pathname: generatePath(AppRoutes.ORGANISATION_DETAILS, { id }),
      queryParams: params,
    });

  const redirectToCompanyDashboard = () =>
    redirect({
      pathname: AppRoutes.COMPANY_DASHBOARD,
    });

  const redirectToProductOverview = (productId?: number) =>
    navigate(generatePath(AppRoutes.PRODUCT_OVERVIEW, { id: productId }));

  const redirectToProductDistribution = (productId?: number) =>
    navigate(
      generatePath(
        `/${AppRoutes.CREATE_PRODUCT}/${AppRoutes.CREATE_PRODUCT_PRODUCT_DISTRIBUTION}`,
        { id: productId }
      )
    );

  const redirectToProductFacility = (productId?: number) =>
    navigate(
      generatePath(
        `/${AppRoutes.CREATE_PRODUCT}/${AppRoutes.CREATE_PRODUCT_PROCESSING_FACILITY}`,
        { id: productId }
      )
    );

  const redirectToProductRawTransport = (productId?: number) =>
    navigate(
      generatePath(
        `/${AppRoutes.CREATE_PRODUCT}/${AppRoutes.CREATE_PRODUCT_RAW_MATERIAL_TRANSPORTATION}`,
        { id: productId }
      )
    );

  const redirectToProductRawMaterial = (productId?: number) =>
    navigate(
      generatePath(
        `/${AppRoutes.CREATE_PRODUCT}/${AppRoutes.CREATE_PRODUCT_RAW_MATERIAL}`,
        { id: productId }
      )
    );

  const redirectToProductListing = () => navigate(AppRoutes.PRODUCT);

  const redirectToChildProducts = (productName?: string) =>
    redirect({
      pathname: generatePath(AppRoutes.CHILD_PRODUCTS),
      state: productName,
    });

  const redirectToCategory1 = (
    id: string,
    params?: { activeTab?: string; isProcessingFacility?: boolean },
    replace?: boolean
  ) =>
    redirect({
      pathname: generatePath(AppRoutes.SCOPE_3_CATEGORY_1, { id }),
      queryParams: params,
      replace,
    });

  const redirectToRespectiveScopeTab = (
    id?: string,
    key?: string,
    activeYear?: string
  ) =>
    redirect({
      pathname: generatePath(`/${AppRoutes.ACTIVITY_DATA_ENTRY}`, { id }),
      queryParams: { activeTab: activeYear, scopeTab: key },
    });

  return {
    location,
    navigate,
    navigateBack,
    redirect,
    redirectToEditOrganization,
    redirectToActivityDataEntry,
    redirectToAddLocations,
    redirectToCompanySetUp,
    redirectToProductOverview,
    redirectToOrganizationDetails,
    redirectToCompanyDashboard,
    redirectToChildProducts,
    redirectToProductDistribution,
    redirectToProductListing,
    redirectToProductFacility,
    redirectToProductRawTransport,
    redirectToProductRawMaterial,
    redirectToCategory1,
    redirectToRespectiveScopeTab,
  };
};

export default useRedirect;
